/**
 * ProtocolStepsPanel - Panel de seguimiento de pasos del protocolo
 * Muestra el progreso en protocolos de secuencia estricta
 */
import React from 'react';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Badge } from './ui/badge';
import { ScrollArea } from './ui/scroll-area';
import { 
  CheckCircle, 
  Circle, 
  ArrowRight,
  Lock,
  Pill,
  FirstAid,
  Stethoscope,
  Warning,
  Shield
} from '@phosphor-icons/react';

const getStepIcon = (tipo, size = 16) => {
  switch (tipo) {
    case 'medicamento':
      return <Pill size={size} />;
    case 'accion':
      return <FirstAid size={size} />;
    case 'examen':
      return <Stethoscope size={size} />;
    default:
      return <Circle size={size} />;
  }
};

const getModeColor = (modo) => {
  switch (modo) {
    case 'estricto':
      return 'bg-red-100 text-red-700';
    case 'observacional':
      return 'bg-yellow-100 text-yellow-700';
    case 'libre':
      return 'bg-green-100 text-green-700';
    default:
      return 'bg-slate-100 text-slate-700';
  }
};

const getModeLabel = (modo) => {
  switch (modo) {
    case 'estricto':
      return 'Secuencia Estricta';
    case 'observacional':
      return 'Observacional';
    case 'libre':
      return 'Libre';
    default:
      return modo;
  }
};

export default function ProtocolStepsPanel({ 
  protocolStatus, 
  onClose,
  collapsed = false 
}) {
  if (!protocolStatus || !protocolStatus.has_protocol) {
    return null;
  }

  const { 
    protocol_name, 
    modo_ejecucion, 
    pasos = [], 
    paso_actual,
    pasos_completados_count = 0,
    total_pasos = 0,
    intentos_bloqueados = []
  } = protocolStatus;

  const progressPercent = total_pasos > 0 ? (pasos_completados_count / total_pasos) * 100 : 0;

  if (collapsed) {
    return (
      <div className="fixed bottom-4 right-4 z-40">
        <Card className="bg-white/95 backdrop-blur shadow-lg border-2 border-blue-200 w-64">
          <CardContent className="p-3">
            <div className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <Shield size={18} className="text-blue-600" />
                <span className="text-sm font-medium">Protocolo Activo</span>
              </div>
              <Badge className={getModeColor(modo_ejecucion)} variant="secondary">
                {getModeLabel(modo_ejecucion)}
              </Badge>
            </div>
            <div className="mt-2">
              <div className="flex justify-between text-xs text-slate-500 mb-1">
                <span>Progreso</span>
                <span>{pasos_completados_count}/{total_pasos}</span>
              </div>
              <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
                <div 
                  className="h-full bg-blue-500 transition-all duration-500"
                  style={{ width: `${progressPercent}%` }}
                />
              </div>
            </div>
            {paso_actual && (
              <div className="mt-2 text-xs">
                <span className="text-slate-500">Siguiente: </span>
                <span className="font-medium text-blue-700">{paso_actual.nombre}</span>
              </div>
            )}
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <Card className="bg-white/95 backdrop-blur shadow-lg border-2 border-blue-200" data-testid="protocol-panel">
      <CardHeader className="pb-2">
        <div className="flex items-center justify-between">
          <CardTitle className="text-base flex items-center gap-2">
            <Shield size={20} className="text-blue-600" />
            {protocol_name || 'Protocolo Clínico'}
          </CardTitle>
          <Badge className={getModeColor(modo_ejecucion)} variant="secondary">
            {getModeLabel(modo_ejecucion)}
          </Badge>
        </div>
        {/* Progress bar */}
        <div className="mt-2">
          <div className="flex justify-between text-xs text-slate-500 mb-1">
            <span>Progreso del Protocolo</span>
            <span>{pasos_completados_count}/{total_pasos} pasos</span>
          </div>
          <div className="h-2 bg-slate-200 rounded-full overflow-hidden">
            <div 
              className="h-full bg-gradient-to-r from-blue-500 to-green-500 transition-all duration-500"
              style={{ width: `${progressPercent}%` }}
            />
          </div>
        </div>
      </CardHeader>
      <CardContent className="pt-0">
        <ScrollArea className="h-[300px] pr-2">
          <div className="space-y-2">
            {pasos.map((paso, index) => (
              <div 
                key={paso.id}
                className={`
                  flex items-start gap-3 p-2 rounded-lg transition-all
                  ${paso.completado ? 'bg-green-50 border border-green-200' : ''}
                  ${paso.es_paso_actual ? 'bg-blue-50 border-2 border-blue-400 shadow-sm' : ''}
                  ${!paso.habilitado && !paso.completado ? 'bg-slate-50 opacity-60' : ''}
                `}
                data-testid={`protocol-step-${paso.orden}`}
              >
                {/* Status icon */}
                <div className="flex-shrink-0 mt-0.5">
                  {paso.completado ? (
                    <CheckCircle size={20} weight="fill" className="text-green-600" />
                  ) : paso.es_paso_actual ? (
                    <ArrowRight size={20} weight="bold" className="text-blue-600 animate-pulse" />
                  ) : !paso.habilitado ? (
                    <Lock size={20} className="text-slate-400" />
                  ) : (
                    <Circle size={20} className="text-slate-400" />
                  )}
                </div>

                {/* Step content */}
                <div className="flex-1 min-w-0">
                  <div className="flex items-center gap-2">
                    <span className={`
                      text-xs font-medium px-1.5 py-0.5 rounded
                      ${paso.completado ? 'bg-green-100 text-green-700' : ''}
                      ${paso.es_paso_actual ? 'bg-blue-100 text-blue-700' : ''}
                      ${!paso.habilitado && !paso.completado ? 'bg-slate-100 text-slate-500' : ''}
                    `}>
                      {paso.orden}
                    </span>
                    {getStepIcon(paso.tipo)}
                    <span className={`
                      text-sm font-medium
                      ${paso.completado ? 'text-green-800' : ''}
                      ${paso.es_paso_actual ? 'text-blue-800' : ''}
                      ${!paso.habilitado && !paso.completado ? 'text-slate-500' : 'text-slate-700'}
                    `}>
                      {paso.nombre}
                    </span>
                  </div>
                  {paso.descripcion && (
                    <p className="text-xs text-slate-500 mt-0.5 line-clamp-1">
                      {paso.descripcion}
                    </p>
                  )}
                  <div className="flex items-center gap-2 mt-1">
                    <Badge variant="outline" className="text-xs py-0">
                      {paso.tipo === 'medicamento' ? 'Medicamento' : 
                       paso.tipo === 'accion' ? 'Acción' : paso.tipo}
                    </Badge>
                    {!paso.obligatorio && (
                      <Badge variant="secondary" className="text-xs py-0 bg-slate-100">
                        Opcional
                      </Badge>
                    )}
                  </div>
                </div>
              </div>
            ))}
          </div>
        </ScrollArea>

        {/* Blocked attempts warning */}
        {intentos_bloqueados.length > 0 && (
          <div className="mt-3 p-2 bg-amber-50 border border-amber-200 rounded-lg">
            <div className="flex items-center gap-2 text-amber-700">
              <Warning size={16} />
              <span className="text-xs font-medium">
                {intentos_bloqueados.length} intento(s) bloqueado(s)
              </span>
            </div>
          </div>
        )}
      </CardContent>
    </Card>
  );
}

// Mini component for showing blocked action dialog
export function ProtocolBlockedDialog({ 
  isOpen, 
  onClose, 
  message, 
  pasoEsperado, 
  severidad 
}) {
  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50">
      <div className="bg-white rounded-lg shadow-xl max-w-md w-full mx-4 p-6">
        <div className="flex items-center gap-3 mb-4">
          <div className="p-2 bg-red-100 rounded-full">
            <Lock size={24} className="text-red-600" />
          </div>
          <div>
            <h3 className="font-semibold text-lg text-slate-900">
              Acción Bloqueada
            </h3>
            <p className="text-sm text-slate-500">
              Protocolo de secuencia estricta
            </p>
          </div>
        </div>

        <div className="mb-4">
          <p className="text-slate-700">{message}</p>
        </div>

        {pasoEsperado && (
          <div className="p-3 bg-blue-50 border border-blue-200 rounded-lg mb-4">
            <p className="text-sm text-blue-700">
              <span className="font-medium">Paso esperado: </span>
              {pasoEsperado.nombre}
            </p>
            <p className="text-xs text-blue-600 mt-1">
              Debe completar este paso antes de continuar.
            </p>
          </div>
        )}

        <div className="flex items-center justify-between">
          <Badge 
            className={
              severidad === 'alta' ? 'bg-red-100 text-red-700' :
              severidad === 'media' ? 'bg-amber-100 text-amber-700' :
              'bg-slate-100 text-slate-700'
            }
          >
            Severidad: {severidad || 'media'}
          </Badge>
          <button
            onClick={onClose}
            className="px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 transition-colors"
          >
            Entendido
          </button>
        </div>
      </div>
    </div>
  );
}
